import type {
  ApplyStaffStatColumnCatalogBody,
  StaffStatCatalogRow,
} from '~/hooks/queries/useStaffStatColumnCatalogQuery/staffStatColumnCatalogSchema';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';

import {
  parseChildLimitDriftDetails,
  parseChildLimitExceedsParentCeilingError,
  type ChildLimitExceedsParentCeilingDetail,
  type ChildLimitExceedsParentCeilingError,
  type StatAccessErrorInput,
  type StatAccessErrorResponse,
} from './statAccessErrorSchema';

export type {
  ChildLimitExceedsParentCeilingDetail,
  ChildLimitExceedsParentCeilingError,
  StatAccessErrorInput,
  StatAccessErrorResponse,
  StatAccessLooseErrorResponse,
} from './statAccessErrorSchema';

export function isChildLimitExceedsParentCeilingError(
  data: StatAccessErrorInput,
): data is ChildLimitExceedsParentCeilingError {
  return parseChildLimitExceedsParentCeilingError(data) != null;
}

export function getChildLimitDriftDetails(
  data: StatAccessErrorInput,
): ChildLimitExceedsParentCeilingDetail[] {
  return parseChildLimitDriftDetails(data) ?? [];
}

export function getExcessCatalogIdsFromDriftDetails(
  driftDetails: readonly ChildLimitExceedsParentCeilingDetail[],
): string[] {
  const ids = new Set<string>();
  for (const detail of driftDetails) {
    detail.excessCatalogIds.forEach((id) => ids.add(id));
  }
  return [...ids];
}

export function getExcessCatalogIdsFromDriftError(data: StatAccessErrorInput): string[] {
  return getExcessCatalogIdsFromDriftDetails(getChildLimitDriftDetails(data));
}

/** Boundary helper for {@link AnalyticsStatsApiError.rawResponse} before stat-access parsing. */
export function toStatAccessErrorInput(rawResponse: unknown): StatAccessErrorInput {
  if (rawResponse == null || typeof rawResponse !== 'object') {
    return undefined;
  }
  return rawResponse as StatAccessErrorResponse;
}

/** Scoped PUT body to drop child columns above the parent ceiling (409 recovery). */
export function buildScopedRealignmentApplyBody(
  catalog: StaffStatCatalogRow[],
  driftDetails: readonly ChildLimitExceedsParentCeilingDetail[],
  analyticsReportingMode?: Genie2ReportingMode,
): ApplyStaffStatColumnCatalogBody | null {
  if (driftDetails.length === 0) return null;

  const statKeys = [...new Set(driftDetails.map((detail) => detail.statKey))];
  const excessCatalogIds = new Set(getExcessCatalogIdsFromDriftDetails(driftDetails));

  const allowedCatalogIds = catalog
    .filter(
      (row) =>
        !excessCatalogIds.has(row.catalogId) &&
        statKeys.some(
          (statKey) =>
            row.statKeys.includes(statKey) && row.effectiveAllowByStatKey[statKey] === true,
        ),
    )
    .map((row) => row.catalogId);

  return analyticsReportingMode != null
    ? { allowedCatalogIds, statKeys, analyticsReportingMode }
    : { allowedCatalogIds, statKeys };
}
